import { MdDarkMode, MdLightMode } from 'react-icons/md';
import clsx from 'clsx';
import { useTema } from '@/contexts/TemaContext';

/**
 * O controle de tema da barra superior.
 *
 * É um `switch` de verdade — trilho e botão deslizante —, e não um botão de
 * ícone, porque é isso que ele é: dois estados, sempre visíveis, sem estado
 * intermediário. Um ícone solitário de lua obrigaria o usuário a descobrir se
 * ele mostra o tema em vigor ou o tema para onde vai; o trilho mostra os dois e
 * a posição responde.
 *
 * Os dois ícones ficam **dentro** do trilho, um em cada extremidade, e o botão
 * deslizante passa por cima deles. Assim o estado não é comunicado só por
 * posição nem só por cor: o ícone que o botão cobre é o do tema em vigor.
 *
 * A troca de volta para "seguir o sistema" não mora aqui — ela é rara e mora no
 * menu do usuário, junto das outras preferências de conta. Ver `UserMenu`.
 */
export default function TemaSwitch() {
  const { tema, alternar } = useTema();
  const escuro = tema === 'escuro';

  return (
    <button
      type="button"
      role="switch"
      aria-checked={escuro}
      aria-label="Tema escuro"
      title={escuro ? 'Mudar para o tema claro' : 'Mudar para o tema escuro'}
      onClick={alternar}
      className="relative inline-flex h-6 w-11 shrink-0 items-center rounded-full border border-line-strong bg-control transition-colors hover:bg-control-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-2 focus-visible:ring-offset-surface"
    >
      {/* Os ícones do trilho são decorativos: o rótulo e o `aria-checked` já
          dizem o que o controle é e em que estado está. */}
      <MdLightMode
        aria-hidden="true"
        size={11}
        className="pointer-events-none absolute left-[5px] text-ink-faint"
      />
      <MdDarkMode
        aria-hidden="true"
        size={11}
        className="pointer-events-none absolute right-[5px] text-ink-faint"
      />
      <span
        aria-hidden="true"
        className={clsx(
          'relative h-[18px] w-[18px] rounded-full bg-surface shadow-sm transition-transform',
          escuro ? 'translate-x-[23px]' : 'translate-x-[3px]',
        )}
      />
    </button>
  );
}
